/*
 * Peel Find Your Fit — the three-question case finder.
 * Loaded only on page templates with the "find-your-fit" suffix.
 *
 * Owns the step state, the recommendation lookup table (which the merchant
 * edits as priority blocks), and resolving the named case to the real
 * product for the device the customer picked.
 */
(function () {
  var LAST_QUESTION = 3;
  var RESULT_STEP = 4;

  var root = document.querySelector('[data-peel-fit]');
  if (!root) return;

  var scope = root.closest('.shopify-section') || document;
  var configEl = scope.querySelector('[data-peel-fit-config]');
  var config;
  if (!configEl) return;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  /* With "List phones from products" on, each brand lists the phones its
     cases declare, catalogued and ordered newest first by
     peel-device-map.js — the list the collection pickers show, so a new
     phone is here as soon as its first case is published. The blocks
     still decide which brands are offered and in what order; a brand no
     case declares, or every brand if the map didn't load, keeps the
     models typed on its block. Labels are deduped because two spellings
     of one phone ("Google Pixel 10", "Pixel 10") key apart but read alike. */
  function declaredBrands(typed) {
    var map = window.peelDeviceMap;
    if (!config.modelsFromProducts || !map) return typed;
    var catalog = map.catalog();

    return typed.map(function (brand) {
      var name = String(brand.name || '').toLowerCase();
      for (var i = 0; i < catalog.length; i++) {
        if (catalog[i].name.toLowerCase() !== name) continue;
        var seen = {};
        var models = [];
        catalog[i].models.forEach(function (entry) {
          if (seen[entry.model]) return;
          seen[entry.model] = true;
          models.push(entry.model);
        });
        return { name: brand.name, models: models };
      }
      return brand;
    });
  }

  var brands = declaredBrands(config.brands || []);
  var state = {
    step: 0,
    brand: brands.length ? brands[0].name : '',
    model: brands.length && brands[0].models.length ? brands[0].models[0] : '',
    priority: null,
    usage: null
  };

  /* The last product we resolved, so Add to cart knows what to post. */
  var matched = null;

  var els = {
    progress: root.querySelector('[data-fit-progress]'),
    progressFill: root.querySelector('[data-fit-progress-fill]'),
    progressLabel: root.querySelector('[data-fit-progress-label]'),
    modelSelect: root.querySelector('[data-fit-model]'),
    steps: root.querySelectorAll('[data-fit-step]'),
    variant: root.querySelector('[data-fit-variant]'),
    variantLabel: root.querySelector('[data-fit-variant-label]'),
    variantSelect: root.querySelector('[data-fit-variant-select]'),
    addBtn: root.querySelector('[data-fit-add]'),
    detailsLink: root.querySelector('[data-fit-details]')
  };

  /* ---- Viewport fill ---- */

  /* The section fills what the header, announcement bar and breadcrumb
     leave of the viewport. Their combined height is whatever sits above
     the section in the document, so measure that rather than hard-coding
     a header height that the sticky header changes on scroll anyway.
     Measured on load and resize only — not on scroll, so the compressing
     header can't make the section twitch. */
  function measureChrome() {
    var top = root.getBoundingClientRect().top + window.pageYOffset;
    root.style.setProperty('--peel-fit-offset', Math.max(0, Math.round(top)) + 'px');
  }

  var measureQueued = false;
  function queueMeasure() {
    if (measureQueued) return;
    measureQueued = true;
    window.requestAnimationFrame(function () {
      measureQueued = false;
      measureChrome();
    });
  }

  /* ---- Steps ---- */

  function render() {
    root.setAttribute('data-step', String(state.step));

    els.steps.forEach(function (el) {
      el.hidden = Number(el.getAttribute('data-fit-step')) !== state.step;
    });

    var onQuestion = state.step >= 1 && state.step <= LAST_QUESTION;
    if (els.progress) els.progress.hidden = !onQuestion;
    if (onQuestion) {
      els.progressFill.style.width = Math.round((state.step / LAST_QUESTION) * 100) + '%';
      els.progressLabel.textContent = (config.stepLabel || 'Step [current] of [total]')
        .replace('[current]', String(state.step))
        .replace('[total]', String(LAST_QUESTION));
    }

    root.querySelectorAll('[data-fit-option]').forEach(function (btn) {
      var answered = state[btn.getAttribute('data-fit-option')];
      btn.setAttribute('aria-pressed', answered === btn.getAttribute('data-value') ? 'true' : 'false');
    });

    root.querySelectorAll('[data-fit-brand]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-fit-brand') === state.brand ? 'true' : 'false');
    });

    var answerFor = { 2: 'priority', 3: 'usage' };
    Object.keys(answerFor).forEach(function (step) {
      var pane = root.querySelector('[data-fit-step="' + step + '"]');
      var next = pane && pane.querySelector('[data-fit-next]');
      if (next) next.setAttribute('aria-disabled', state[answerFor[step]] ? 'false' : 'true');
    });
  }

  /* Move the step, then put focus on the new heading so the change is
     announced and keyboard users don't land back at the top of the page. */
  function goTo(step) {
    var previous = state.step;
    state.step = step;
    render();
    if (step === RESULT_STEP) showResult();

    var pane = root.querySelector('[data-fit-step="' + step + '"]');
    var heading = pane && pane.querySelector('[data-fit-focus]');
    if (heading && previous !== step) heading.focus({ preventScroll: true });
  }

  /* ---- Device ---- */

  function modelsFor(brandName) {
    for (var i = 0; i < brands.length; i++) {
      if (brands[i].name === brandName) return brands[i].models || [];
    }
    return [];
  }

  function populateModels() {
    if (!els.modelSelect) return;
    var models = modelsFor(state.brand);
    els.modelSelect.innerHTML = '';
    models.forEach(function (model) {
      var opt = document.createElement('option');
      opt.value = model;
      opt.textContent = model;
      els.modelSelect.appendChild(opt);
    });
    state.model = models.length ? models[0] : '';
    els.modelSelect.value = state.model;
  }

  /* ---- Recommendation ---- */

  function recommend() {
    var rule = config.priorities[state.priority];
    var keys = Object.keys(config.cases);
    if (!rule) return [keys[0], keys[1] || keys[0]];

    if (rule.overrideUsage && rule.overrideUsage === state.usage && rule.overridePrimary) {
      return [rule.overridePrimary, rule.overrideSecondary || rule.secondary];
    }
    return [rule.primary, rule.secondary];
  }

  function caseFor(key) {
    return config.cases[key] || config.cases[Object.keys(config.cases)[0]];
  }

  /* ---- Result ---- */

  function setText(selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value || '';
  }

  function fillList(selector, items, className) {
    var host = root.querySelector(selector);
    if (!host) return;
    host.innerHTML = '';
    (items || []).forEach(function (item) {
      var div = document.createElement('div');
      div.className = className;
      div.textContent = item;
      host.appendChild(div);
    });
  }

  function setImage(src, alt) {
    var img = root.querySelector('[data-fit-primary-image]');
    var empty = root.querySelector('[data-fit-primary-image-empty]');
    if (!img) return;
    if (src) {
      img.src = src;
      img.alt = alt;
      img.hidden = false;
      if (empty) empty.hidden = true;
    } else {
      img.hidden = true;
      img.removeAttribute('src');
      if (empty) empty.hidden = false;
    }
  }

  function showResult() {
    var pair = recommend();
    var primary = caseFor(pair[0]);
    var secondary = caseFor(pair[1]);

    var priority = config.priorities[state.priority] || {};
    var usage = config.usages[state.usage] || {};

    setText('[data-fit-result-title]', primary.name + ' ' + (config.resultTitleSuffix || ''));
    setText(
      '[data-fit-result-sub]',
      (config.resultSubTemplate || '')
        .replace('[model]', state.model)
        .replace('[priority]', priority.summary || '')
        .replace('[usage]', usage.summary || '')
    );

    setText('[data-fit-primary-name]', primary.name);
    setText('[data-fit-primary-desc]', primary.description);
    fillList('[data-fit-primary-specs]', primary.specs, 'peel-fit-spec');
    fillList('[data-fit-reasons]', primary.reasons, 'peel-fit-reason');

    setText('[data-fit-secondary-name]', secondary.name);
    setText('[data-fit-secondary-price]', secondary.price ? '· ' + secondary.price : '');
    setText('[data-fit-secondary-desc]', secondary.description);
    var secondaryLink = root.querySelector('[data-fit-secondary-link]');
    if (secondaryLink) secondaryLink.href = secondary.url || '#';

    /* Show the merchant's own copy straight away, then upgrade it in place
       once the real product for this device comes back. */
    applyFallback(primary);
    resolveProduct(primary, state.model);
  }

  function applyFallback(caseData) {
    matched = null;
    setText('[data-fit-primary-price]', caseData.price);
    setImage(caseData.image, caseData.name);
    if (els.variant) els.variant.hidden = true;
    if (els.addBtn) els.addBtn.hidden = true;
    if (els.detailsLink) els.detailsLink.href = caseData.url || '#';
  }

  function applyProduct(product, caseData) {
    matched = product;
    setText('[data-fit-primary-price]', product.price);
    setImage(product.image || caseData.image, product.title);
    if (els.detailsLink) els.detailsLink.href = product.url;

    var variants = (product.variants || []).filter(function (v) {
      return v.available;
    });

    if (els.variantSelect) {
      els.variantSelect.innerHTML = '';
      variants.forEach(function (variant) {
        var opt = document.createElement('option');
        opt.value = String(variant.id);
        opt.textContent = variant.title;
        els.variantSelect.appendChild(opt);
      });
    }
    if (els.variant) els.variant.hidden = variants.length < 2;
    if (els.variantLabel) els.variantLabel.textContent = product.optionName || '';

    if (els.addBtn) {
      var addable = product.available && (variants.length > 0 || product.defaultVariantId);
      els.addBtn.hidden = !addable;
      els.addBtn.disabled = false;
    }
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Products are titled "<family> <device> Case", so a match is a title that
     ends with the chosen model — that way "iPhone 16 Pro" never picks up the
     "iPhone 16 Pro Max" case. */
  function pickMatch(products, caseData, model) {
    var types = (caseData.productTypes || []).map(function (t) {
      return t.toLowerCase();
    });
    var endsWithModel = new RegExp(escapeRegExp(model.toLowerCase()) + '(\\s+case)?$');

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var type = (product.type || '').toLowerCase();
      if (types.length && types.indexOf(type) === -1) continue;
      if (!endsWithModel.test((product.title || '').toLowerCase().trim())) continue;
      return product;
    }
    return null;
  }

  function resolveProduct(caseData, model) {
    if (!model) return;
    var requestFor = model + '|' + caseData.name;
    resolveProduct.pending = requestFor;

    var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url =
      routes +
      'search/suggest?q=' +
      encodeURIComponent(caseData.name + ' ' + model) +
      '&resources[type]=product&resources[limit]=10&section_id=peel-fit-lookup';

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        var el = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-peel-fit-lookup]');
        return el ? JSON.parse(el.textContent) : [];
      })
      .then(function (products) {
        // A later answer may have overtaken this request.
        if (resolveProduct.pending !== requestFor) return;
        var product = pickMatch(products, caseData, model);
        if (product) applyProduct(product, caseData);
      })
      .catch(function () {
        /* Keep the fallback copy already on screen. */
      });
  }

  /* ---- Cart ---- */

  function addToCart() {
    if (!matched || !els.addBtn) return;
    var variantId =
      els.variant && !els.variant.hidden && els.variantSelect.value
        ? els.variantSelect.value
        : matched.defaultVariantId;
    if (!variantId) return;

    var label = els.addBtn.textContent;
    els.addBtn.disabled = true;

    var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(routes + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ['cart-drawer'] })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var drawerSection = document.getElementById('shopify-section-cart-drawer');
        if (drawerSection && cart.sections && cart.sections['cart-drawer']) {
          drawerSection.outerHTML = cart.sections['cart-drawer'];
        }
        var drawer = document.getElementById('cartDrawer');
        if (drawer && typeof drawer.showModal === 'function') drawer.showModal();
        /* cart/add.js sends back the added lines, not the cart, so
           item_count is undefined here; _updateCartItemCount then reads the
           count off the drawer this response re-rendered. */
        if (typeof _updateCartItemCount === 'function') {
          _updateCartItemCount(cart.item_count);
        }
        els.addBtn.disabled = false;
        els.addBtn.textContent = label;
      })
      .catch(function () {
        els.addBtn.disabled = false;
        els.addBtn.textContent = label;
      });
  }

  /* ---- Wiring ---- */

  root.addEventListener('click', function (event) {
    var target = event.target.closest(
      '[data-fit-start],[data-fit-next],[data-fit-back],[data-fit-restart],[data-fit-option],[data-fit-brand],[data-fit-add]'
    );
    if (!target || !root.contains(target)) return;

    if (target.hasAttribute('data-fit-start')) {
      goTo(1);
    } else if (target.hasAttribute('data-fit-next')) {
      if (target.getAttribute('aria-disabled') === 'true') return;
      goTo(Math.min(RESULT_STEP, state.step + 1));
    } else if (target.hasAttribute('data-fit-back')) {
      goTo(Math.max(1, state.step - 1));
    } else if (target.hasAttribute('data-fit-restart')) {
      state.priority = null;
      state.usage = null;
      goTo(0);
    } else if (target.hasAttribute('data-fit-brand')) {
      state.brand = target.getAttribute('data-fit-brand');
      populateModels();
      render();
    } else if (target.hasAttribute('data-fit-option')) {
      state[target.getAttribute('data-fit-option')] = target.getAttribute('data-value');
      render();
    } else if (target.hasAttribute('data-fit-add')) {
      addToCart();
    }
  });

  if (els.modelSelect) {
    els.modelSelect.addEventListener('change', function () {
      state.model = els.modelSelect.value;
    });
  }

  window.addEventListener('resize', queueMeasure);
  /* Late webfonts and images can still resize the header after parse. */
  window.addEventListener('load', queueMeasure);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueMeasure);

  measureChrome();
  populateModels();
  render();
})();
